import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { IProduct } from '@rogue/universal'
import { isEmptySlingerXSideBundle } from '../pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXUtils'
import { messages } from '@/js/app-bridge/messages'
import { findDuplicateItem } from '@/js/stores/list-fns'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import { useUIStore } from '@/js/stores/uiStore'
import { findCustomProductSiblings, findCustomProductVariantByMainSku, isCustomProductAddCountExcluded, isCustomProductQtyExcluded } from '@/js/customProducts/registry'
import type { List, ListType, Message, ShortcutListType } from '@/js/stores/listTypes'
import type { BuyRequest, IRogue, Product } from '@/js/services/RogueTypes'

export const useListStore = defineStore('lists', () => {
  function sendItemUpdate(rogue: IRogue, list: List, listItemId: string) {
    if (list && listItemId) {
      const item = list.items.find(itm => itm._id === listItemId)

      if (item) {
        rogue.fetch(`/svc_list/lists/${list._id}/items/${listItemId}`, 'PUT', item)
          .catch(err => rogue.log.error('Failed to update list item', err as Error))
      }
    }
  }

  // Check if this list should be deleted on load - e.g. empty registries, non-needed buy-nows
  function shouldDeleteList(list: List, rogue: IRogue) {
    if (list.type === 'buyNow') {
      /**
       * `buyNow` lists are only saved if you go directly from the item to checkout. So if we are not on those pages
       * (e.g. we started a buyNow checkout but then went back to the homepage) we should delete that list.
       */

      const path = rogue.ssr ? '' : window.location.pathname

      // The buyNow process requires login, so we need to keep them here too
      return !(path.includes('/checkout') || path.includes('/login') || path.includes('/preloader'))
    }
    if (list.type === 'registryPurchase') {
      // Sometimes registry lists are created incorrectly...
      return !list.metadata?.registry || !list.items?.filter(item => !!item).length
    }

    return false
  }

  const MESSAGE_TIMEOUT_TIME = 6000

  const LIST_DISPLAY_NAME: {[key: string]: string} = {
    cart : 'cart',
    sfl  : 'Save for Later',
  }

  let initPromise: Promise<void> | undefined

  const debouncedUpdates:{[key: string]: undefined | ReturnType<typeof setTimeout>} = {}

  function emptyList(listType: ListType): List {
    return { type: listType, items: [], messages: [] }
  }

  const customerStore = useCustomerStore()
  const productStore = useProductStore()

  let loaded = false

  const lists = ref<{[key: string]: List}>({})
  const allLists = ref<List[]>([])
  const cart = ref<List>(emptyList('cart'))
  const quote = ref<List>(emptyList('quote'))
  const sfl = ref<List>(emptyList('sfl'))
  const buyNow = ref<List>(emptyList('buyNow'))
  const wishlist = ref<List>(emptyList('wishlist'))
  const shortcuts = ['cart', 'quote', 'sfl', 'buyNow', 'wishlist']
  const missedDeals = ref<BuyRequest[]>([])

  const cartQty = computed<number>(() => cart.value.items
    // Excludes custom-product sibling/hardware lines already counted via their base unit — purpose-keyed per registry variant.
    .filter(item => !isCustomProductQtyExcluded(item, productStore.products[item.productId]?.sku))
    .reduce((totalQty, item) => totalQty + (item.qty || 1), 0) || 0)

  const registries = computed(() => allLists.value.filter(l => l.type === 'registry'))
  const registryPurchases = computed(() => allLists.value.filter(l => l.type === 'registryPurchase'))

  function getShortCut(listType: string): Ref<List> | undefined {
    switch (listType) {
      case 'cart':
        return cart
      case 'quote':
        return quote
      case 'sfl':
        return sfl
      case 'buyNow':
        return buyNow
      case 'wishlist':
        return wishlist
      default:
        return undefined
    }
  }

  function clear() {
    lists.value = {}
    allLists.value = []
    cart.value = emptyList('cart')
    sfl.value = emptyList('sfl')
    quote.value = emptyList('quote')
    buyNow.value = emptyList('buyNow')
    wishlist.value = emptyList('wishlist')
  }

  /**
   * Switch which list is currently the active sidecart. This can be used for registry lists.
   * If no `listId` is provided, it will switch back to the default quote
   */
  function switchActiveCart(rogue: IRogue, listId?: string) {
    if (!loaded) {
      // Dont set the active cart until lists have all loaded
      return
    }

    const list = listId && lists.value[listId] ? lists.value[listId] : quote.value
    if (list !== cart.value) {
      // Set the cart, which will update the sidecart and cart page, and which list is added to from RHPA
      cart.value = list

      // Reset the order on login, with new customer info
      if (rogue.loaded.checkout) {
        import('@/js/pages/checkout/checkout').then(fns => fns.resetOrder(rogue))
      }

      // If we reload the browser, we need to know which list was just set to be the active cart. So we save a property to the metadata object
      if (list.type !== 'quote' || allLists.value.some(l => l.metadata?.activeCartTs)) {
        list.metadata = list.metadata || { isActive: true }

        list.metadata.activeCartTs = Date.now()
        if (list._id) {
          rogue.fetch(`/svc_list/lists/${list._id}/metadata`, 'PUT', list.metadata)
        }
      }
    }
  }

  async function addList(rogue: IRogue, list: List) {
    // We only care about "active" lists. These should be filtered out by the backend, but we can double check here.
    if (!list?.metadata?.isActive) {
      return
    }

    // Each list can technically have its own messages. @TODO we could refactor messaging out of this service...
    list.messages = list.messages || []

    // Items are usually sorted oldest on top - we want newest on top. (Reverse modifies the original array)
    // @TODO are we calling reverse twice? once on load, and again here?
    list.items = list.items.filter(item => !!item).reverse()

    // In app webview: strip program/eBook items locally (remain on server for browser checkout)
    if (rogue.appContext && !rogue.ssr && list.items.length) {
      await productStore.loadAll(list.items.map(i => i.productId), rogue)
      list.items = list.items.filter(item => !getSimpleForBuyRequest(item, rogue)?.isProgram)
    }

    // Save to the `lists` reference object in case we need to easily find this list by ID
    if (list._id) {
      lists.value[list._id] = list
    }
    // Add to the array as well - sometimes we need a reactive list of all our carts
    allLists.value.push(list)

    // If we just created a new list of one of the "buying" types, it should become the default cart
    if (list.type === 'buyNow' || list.type === 'quote' || list.type === 'registryPurchase') {
      switchActiveCart(rogue, list._id)
    }

    // Add the "shortcut" reference (not all types have shortcuts)
    if (list.type && shortcuts.includes(list.type)) {
      getShortCut(list.type)!.value = list
    }
  }

  // Update a list with new metadata, items, or messages. Can also deactivate a list if passed {metadata: {isActive: false}}
  async function updateList(rogue: IRogue, updates: Partial<List> & {list: string}) {
    const existingList: List = getShortCut(updates.list)?.value || lists.value[updates.list]

    if (!existingList) {
      return
    }

    if (updates.metadata?.isActive === false) {
      // We are "deactivating" this list. We should no longer track it in our local state, so it needs to be removed.
      if (existingList._id) {
        delete lists.value[existingList._id]
      }

      // Remove from allLists array
      allLists.value = allLists.value.filter(l => l._id !== existingList._id)

      const wasActiveCart = cart.value === existingList

      // Remove from the `type` shortcut - lists.cart, lists.wishlist, etc
      if (existingList.type && getShortCut(existingList.type)?.value === existingList) {
        getShortCut(existingList.type)!.value = emptyList(existingList.type)
      }

      if (wasActiveCart) {
        switchActiveCart(rogue)
      }
    } else {
      // In app webview: strip program/eBook items from incoming items (remain on server for browser checkout)
      if (rogue.appContext && !rogue.ssr && updates.items?.length) {
        await productStore.loadAll(updates.items.map(i => i.productId), rogue)
        updates.items = updates.items.filter(item => !getSimpleForBuyRequest(item, rogue)?.isProgram)
      }

      // Update the existing list with any updates
      // @TODO this seems a bit excessive, can we pull specific properties out?
      Object.assign(existingList, updates)
    }

    if (updates.list === 'cart') {
      missedDeals.value = missedDeals.value.filter(d => cart.value.items.find(i => getSimpleForBuyRequest(i, rogue)?.productId === getSimpleForBuyRequest(d, rogue)?.productId))
    }
  }

  /**
   * This action can be `await`ed by any components that need to make sure the customer has loaded AND the lists have been loaded
   * from the network. It will first wait for the customer store to load (or create) the session, then will make a call to load the lists.
   *
   * It will only create one "init" promise regardless of how many times its called, so its OK to dispatch it multiple times.
   */
  function init(rogue: IRogue) {
    // SSR does not load lists, since it does not add anything to cart, etc
    if (rogue.ssr) {
      return Promise.resolve(true)
    }

    if (initPromise) {
      return initPromise
    }

    // We only clear this promise if it errors, or the customer is no longer who it was before
    rogue.log.crumb('lists', 'List init started')

    // We need to make sure that the customer store has created a session before calling this method
    // @todo unauthenticated sessions could return an empty set of lists from the list store, then we wouldn't need this await
    initPromise = customerStore.init(rogue)
    // Only load the active lists
      .then(() => rogue.fetch<List[]>('/svc_list/lists?active=true'))
      .then(async listsResp => {
        rogue.log.crumb('lists', `${listsResp.length} lists loaded`)
        loaded = false

        if (listsResp && Array.isArray(listsResp)) {
          await Promise.all(listsResp.map(list => {
            // Delete inactive buyNows, and empty registryPurchase lists
            if (shouldDeleteList(list, rogue)) {
              rogue.fetch(`/svc_list/lists/${list._id}`, 'DELETE')

              return
            }
            // Add this list to the local state

            return addList(rogue, list)
          }))
        }

        loaded = true

        // Find the best "Active Cart" after all lists are loaded
        const activeCart = listsResp
          ?.filter(list => !shouldDeleteList(list, rogue))
          .reduce<List | undefined>((best, list) => {
            if (list.type === 'quote' && !best) return list
            if ((list.metadata?.activeCartTs || 0) > (best?.metadata?.activeCartTs || 0)) return list
            
            return best
          }, undefined)

        if (activeCart) {
          switchActiveCart(rogue, activeCart._id)
        }
      })
      .catch(err => {
        rogue.log.error('Error loading lists', err)
        initPromise = undefined
      })

    return Promise.resolve(initPromise)
  }

  /**
   * Send the current cart items to the checkout store
   */
  async function sendCartItems(rogue: IRogue) {
    // Only dispatch IF the checkout store is loaded (its asynchronous) and all of our cart's items are real items
    // Items sometimes are added to the cart before they have a mongoId, which throws off parts of checkout
    if (rogue.loaded.checkout && cart.value.items.every(i => i._id)) {
      const { initOrder } = await import('@/js/pages/checkout/checkout')
      const order = await initOrder(rogue)
      if (cart.value._id && order.data.listId !== cart.value._id) {
        order.setListId(cart.value._id)
      }
      order.setItems(cart.value.items)
    }

    // If you are on a checkout page and removed all the items from your order, redirect to the cart page
    if (!cart.value.items.length && rogue.router?.currentRoute.value.path.startsWith('/checkout')) {
      rogue.router?.push('/cart')
    }
  }

  async function updateListItem(rogue: IRogue, list: string = 'cart', listItemId: string | undefined, update: Partial<BuyRequest>, debounced: boolean = true) {
    // Callers pass presentational list names too ('bundle'), which resolve to no list at all
    const listObj: List | undefined = getShortCut(list)?.value || lists.value[list]
    const currentItem = listObj?.items.find(i => i._id === listItemId || i.listItemId === listItemId)

    if (!listObj || !currentItem) {
      return
    }

    // Captured before Object.assign overwrites it below — distinguishes a real qty transition from a 0->N seed (e.g. a ?rid=-restored recipe), which must not resurrect a deliberately-zeroed sibling.
    const previousQty = currentItem.qty

    Object.assign(currentItem, update)

    await productStore.load(currentItem.productId, rogue)
    await productStore.loadAll(listObj.items.map(i => i.productId), rogue)

    // A custom product's lead item qty change cascades to every sibling of the same purchase (Cave/Slinger X sides, MS/MMS addon lines, Hybrid units).
    if (previousQty && update.qty) {
      const newQty = update.qty

      findCustomProductSiblings(rogue, currentItem, listObj.items)
        .filter(sibling => sibling.qty !== 0)
        .forEach(sibling => updateListItem(rogue, list, sibling._id || sibling.listItemId, { qty: newQty }))
    }

    if (listObj === cart.value) {
      sendCartItems(rogue)
    }

    const id = listObj._id
    if (id && listItemId) {
      if (debounced) {
        clearTimeout(debouncedUpdates[listItemId])
        debouncedUpdates[listItemId] = setTimeout(() => {
          sendItemUpdate(rogue, lists.value[id], listItemId)
          debouncedUpdates[listItemId] = undefined
        })
      } else {
        sendItemUpdate(rogue, listObj, listItemId)
      }
    }
  }

  async function createList(rogue: IRogue, listType: ListType, metadata: List['metadata'] = { isActive: true }) {
    if (!listType) {
      throw new Error('Cannot create list without type')
    }

    if (metadata.isActive === undefined) {
      metadata.isActive = true
    }

    if (customerStore.email) {
      metadata.email = customerStore.email?.toLowerCase()
    }

    if (listType === 'buyNow') {
      metadata.activeCartTs = Date.now()
    }

    try {
      metadata.rsAnonId = window.rudderanalytics.getAnonymousId()
    } catch (e) {
      rogue.log?.warn('Unable to add rudderId', { err: e })
    }

    const list = await rogue.fetch<List>('/svc_list/lists', 'POST', {
      type: listType === 'cart' ? 'quote' : listType,
      metadata,
    })

    list.items = getShortCut(listType)?.value?.items || []
    list.messages = getShortCut(listType)?.value?.messages || []

    await addList(rogue, list)

    const processListItems = list.items.length
      ? rogue.fetch<{items: BuyRequest[]}>(`/svc_list/lists/${list._id}/items`, 'POST', { items: list.items })
        .then(async (res) => {
          await updateList(rogue, { list: listType, items: res.items.reverse() })

          if (list === cart.value) {
            await sendCartItems(rogue)
          }
        })
      : sendCartItems(rogue)

    if (listType === 'buyNow') {
      await processListItems
    }
  }

  /**
   * Add a message to the specified list.
   */
  function addMessage(rogue: IRogue, data: { list?: string, type?: string, status?: string, productId?: number, text?: string, destination?: string}) {
    if (!data.list) {
      data.list = 'cart'
    }

    const message: Message = {
      type        : data.type || '',
      productId   : data.productId,
      text        : data.text || '',
      status      : data.status,
      destination : data.destination,
    }

    const listObj: List = getShortCut(data.list)?.value || lists.value[data.list]

    // Reset the `direct` or `split` cart messages
    if (data.list === 'cart') {
      listObj.messages = (listObj.messages || []).filter(msg => !['direct', 'split'].includes(msg.type) && msg.text !== data.text)
    }

    updateList(rogue, { list: listObj._id || '', messages: (listObj.messages || []).concat(message) })

    setTimeout(() => {
      updateList(rogue, {
        list     : listObj._id || '',
        messages : (listObj.messages || []).filter(m => JSON.stringify(m) !== JSON.stringify(message)),
      })
    }, MESSAGE_TIMEOUT_TIME)
  }

  async function syncActiveCart(rogue: IRogue): Promise<void> {
    const listId = cart.value._id

    if (!listId) {
      return
    }

    try {
      const list = await rogue.fetch<List>(`/svc_list/lists/${listId}`, 'GET')

      if (!Array.isArray(list?.items)) {
        throw Object.assign(new Error('List not found'), { status: 404 })
      }

      await updateList(rogue, { list: 'cart', items: list.items.reverse() })
    } catch (err) {
      if ((err as { status?: number }).status !== 404) {
        rogue.log.error('Failed to sync cart', err as Error)

        return
      }

      // The list is gone server side - drop the dead id (and its stale items) so the add creates a
      // new list instead of POSTing items into a 404
      delete lists.value[listId]
      allLists.value = allLists.value.filter(l => l._id !== listId)
      cart.value._id = undefined
      cart.value.items = []
    }
  }

  /**
   * Add items to the specified list. This action will also create the list if it doesn't exist yet
   * @param {'cart'|'sfl'|'buyNow'|'wishlist'} list
   * @param {boolean} openSidecart By default, when adding to the cart, the sidecart will show. Set to false to prevent.
   * @param {boolean} showMessages By default, messages will be added to the list's messages array. This property can prevent it.
   * @param {BuyRequest[]} items Buy requests to add. These can be groups as well - which will be split into individual items.
   * @returns {Promise<void>}
   */
  async function addListItems(rogue: IRogue, list: string, items: BuyRequest[], openSidecart: boolean = true, showMessages: boolean = true) {
    // In app webview: another tab's webview may have changed the cart since this one loaded it,
    // so re-sync before we look for duplicates below
    if (rogue.appContext && !rogue.ssr && list === 'cart') {
      await syncActiveCart(rogue)
    }

    const listObj: List = getShortCut(list)?.value || lists.value[list]

    // If we are adding to the 'cart'...
    const switchBackToDefaultQuote = list === 'cart'
      // and the currently active cart is for a registry...
      && listObj.type === 'registryPurchase'
      // and the item(s) we are adding are NOT for that registry
      && !items.some(item => item.metadata?.registry?.registryId === listObj.metadata?.registry?.registryId)

    // Switch back to the "normal" cart
    if (switchBackToDefaultQuote) {
      switchActiveCart(rogue)
    }

    const listIsMyRegistry = listObj?.type === 'registry'

    // Copy the items, so we dont modify state that someone else is watching
    // buyRequests is the data we will send to the backend. We need to filter out `groups` since those are processed
    let buyRequests = JSON.parse(JSON.stringify(items)).filter((item: BuyRequest) => item.type !== 'group') as BuyRequest[]

    // If any buy requests are groups, spread the children out
    items
      .filter(br => br.type === 'group')
      .forEach(br => {
        br.children?.forEach(child => {
          buyRequests.push(Object.assign({}, child, { qty: br.qty * child.qty }))
        })
      })

    // If any of the buy requests are bundles, remove items without qty
    buyRequests.filter(br => br.type === 'bundle')
      .forEach(br => br.children = br.children?.filter(c => c.qty > 0))

    // Remove any invalid buy requests, plus Slinger X side bundles left with no attachments
    buyRequests = buyRequests.filter(br => br && br.qty > 0 && !isEmptySlingerXSideBundle(rogue, br))

    if (listIsMyRegistry) {
      // Update registry settings
      buyRequests.forEach(br => {
        br.metadata = br.metadata || {}
        // @ts-ignore
        br.metadata.registry = br.metadata.registry || { }
        // @ts-ignore
        br.metadata.registry.wanted = br.qty
        // @ts-ignore
        br.metadata.registry.purchased = 0
        // @ts-ignore
        br.metadata.registry.priority = br.metadata.registry.priority || 'medium'
      })
    }

    const newItems:BuyRequest[] = []

    buyRequests.forEach(item => {
      const dupe = findDuplicateItem(item, listObj.items)
      if (dupe) {
        const targetProduct = dupe.children?.[0]?.productId || dupe.productId
        const product = productStore.products[targetProduct] as Product
        const update: Partial<BuyRequest> = { qty: item.qty + dupe.qty }
        if (product.inventory && product.inventory.max && update.qty !== undefined) {
          update.qty = Math.min(product.inventory.max, update.qty)
        }

        if (listIsMyRegistry && item.metadata?.registry?.wanted !== undefined) {
          item.metadata.registry.wanted += dupe.qty
          update.metadata = item.metadata
          dupe.metadata = item.metadata
        }

        updateListItem(rogue, listObj._id, dupe._id, update, false)
      } else {
        newItems.push(item)
      }
    })

    if (openSidecart && list === 'cart' && showMessages === true) {
      const count = buyRequests
        .filter(item => !isCustomProductAddCountExcluded(productStore.products[item.productId]?.sku)).length

      if (count === 1) {
        addMessage(rogue, { list, productId: buyRequests[0].productId, type: 'add', destination: LIST_DISPLAY_NAME[list] })
      } else {
        addMessage(rogue, {
          list, text: `${count} products have been added to your cart`, type: 'multiAdd',
        })
      }
    }

    if (newItems.length > 0) {
      await updateList(rogue, { list, items: newItems.concat(listObj.items) })
    }

    if (openSidecart && !rogue.appContext && list === 'cart') {
      const uiStore = useUIStore(rogue.pinia)
      uiStore.showOverlay('side-cart', rogue)
    }

    if (['wishlist', 'sfl'].indexOf(list) !== -1 && !customerStore.loggedIn) {
      if (!rogue.appContext) {
        rogue.router?.push('/login?redirect=/account/wishlist')
      } else if (rogue.isApp()) {
        rogue.emitToApp(messages.LOGIN, { redirect: 'account/wishlist' })
      }
    }

    if (newItems.length > 0) {
      if (listObj._id) {
        // Await the POST so a move doesn't report complete before the server persists the add
        try {
          const res = await rogue.fetch<{items: BuyRequest[]}>(`/svc_list/lists/${listObj._id}/items`, 'POST', { items: newItems })
          
          await updateList(rogue, { list, items: res.items.reverse() })
          await sendCartItems(rogue)
        } catch (err) {
          rogue.log.error('Failed to add list items', err as Error)
        }
      } else {
        await createList(rogue, list as ListType)
      }
    }

    if (listObj === cart.value || list === 'cart') {
      rogue.track.track({ type: 'addToCart', items: buyRequests })
    } else if (listObj === wishlist.value) {
      rogue.track.track({ type: 'addToWishlist', items: buyRequests })
    }
  }

  /**
   * When the customer changes, we need to basically wipe out our entire state and start over.
   * This is a "global" action dispatched from the Customer store when a customer either logs in or out.
   * (Global actions can be listened to by multiple stores, by adding the `root: true` property to the action)
   */
  async function handleCustomerChange(rogue: IRogue, email: string | null, customerId: string | null) {
    rogue.log.crumb('customer', `Change customer to ${email || customerId || 'guest'} - reloading lists`)
    // Reset init so that the init call will reload, then clear all active lists

    // If the new session is authenticated, save the current state in case we need to merge it
    const activeLists = customerId ? Object.values(lists.value) : []

    // Clear out all the current lists
    clear()

    // Clear the initPromise, so that the `init` action will reload all the lists for the new session / customer
    initPromise = undefined
    await init(rogue)

    if (customerId) {
      // Find the active lists and merge them into the new customers lists. This could be for wishlist items and/or cart items.
      const toMerge = activeLists
        .filter(l => l?.type && l.metadata?.isActive && l.items.length && ['wishlist', 'sfl', 'quote', 'buyNow'].includes(l.type))
      await Promise.all(toMerge.map(list => addListItems(rogue, list.type, list.items, false)))
    }
  }

  /**
   * If promos change, we may need to update the checkout state
   */
  function handleUpdatedPromos(rogue: IRogue, affectedProductIds: number[]) {
    // We only need to update the cart if any of the current cart items are affected
    const isAffected = (listItem: BuyRequest): boolean => affectedProductIds.includes(listItem.productId) || (listItem.children || []).some(child => isAffected(child))

    if (cart.value.items.some(item => isAffected(item))) {
      sendCartItems(rogue)
    }
  }

  async function deleteList(rogue: IRogue, list: string) {
    const listObj: List = getShortCut(list)?.value || lists.value[list]

    if (!listObj?._id) {
      return
    }

    if (listObj.metadata) {
      listObj.metadata.isActive = false
    }
    await updateList(rogue, { list, metadata: listObj.metadata })

    await rogue.fetch(`/svc_list/lists/${listObj._id}`, 'DELETE')
  }

  async function removeFromListItems(rogue: IRogue, list: string, listItemId: string, showMessages: boolean = true) {
    const listObj: List = getShortCut(list)?.value || lists.value[list]

    const listItem = listObj.items.find(item => item._id === listItemId)

    await updateList(rogue, { list, items: listObj.items.filter(item => item !== listItem) })

    // Await the delete so callers (moveListItems) wait for server confirmation
    // a lost/slow DELETE otherwise lets a refreshCart()/reload resurrect the item into the cart
    if (listObj._id) {
      await rogue.fetch(`/svc_list/lists/${listObj._id}/items/${listItemId}`, 'DELETE')
        .catch(err => rogue.log.error('Failed to delete list item', err as Error))
    }

    if (listObj.type === 'registryPurchase' && listObj.items.length === 0 && listObj._id) {
      // Removed the last item from the registry purchase list - delete the list
      await deleteList(rogue, listObj._id)

      return
    }

    if (!listItem) {
      return
    }

    if (listObj === cart.value) {
      sendCartItems(rogue)
      rogue.track.track({ type: 'removeFromCart', items: [listItem] })
    }

    if (showMessages === true) {
      addMessage(rogue, {
        type        : 'remove',
        destination : LIST_DISPLAY_NAME[list],
        productId   : listItem.productId,
      })
    }
  }

  async function moveListItems(rogue: IRogue, listTo: ListType, listFrom: string, items: BuyRequest[]) {
    // M1-migrated configurable products can have child qty > 1; child qty must always be 1
    items
      .filter(item => productStore.products[item.productId]?.type === 'configurable' && item.children?.length === 1 && item.children[0].qty > 1)
      .forEach(item => { item.children![0].qty = 1 })

    const toAdd = items.map(item => {
      const copy = JSON.parse(JSON.stringify(item))
      copy._id = undefined

      return copy as BuyRequest
    })

    await Promise.all([
      addListItems(rogue, listTo, toAdd, false, false),
      ...items.map(item => removeFromListItems(rogue, listFrom, item._id || item.listItemId || '', false)),
    ])

    if (items.length === 1) {
      await productStore.loadAll(items.map(i => i.productId), rogue)
      if (!items[0].metadata?.recipe || !!findCustomProductVariantByMainSku(productStore.products[items[0].productId].sku)) {
        addMessage(rogue, {
          destination : listTo ? LIST_DISPLAY_NAME[listTo] : undefined,
          productId   : items[0].productId,
          type        : 'move',
        })
      }
    } else {
      await productStore.loadAll(items.map(i => i.productId), rogue)

      // Excludes a custom product's addon/side lines the same way cartQty/sflCount do, so this matches what the customer already sees there.
      const movedCount = items
        .filter(item => !isCustomProductQtyExcluded(item, productStore.products[item.productId]?.sku))
        .reduce((sum, item) => sum + (item.qty || 1), 0)

      // movedCount can now be 1 (a custom product's siblings all excluded down to the lead) — this branch never saw that before the exclusion above.
      const destination = listTo ? LIST_DISPLAY_NAME[listTo] : ''
      const text = movedCount === 1
        ? `1 product has been moved to ${destination}`
        : `${movedCount} products have been moved to ${destination}`

      addMessage(rogue, {
        list : listTo,
        text,
        type : 'multiMove',
      })
    }
  }

  async function updateMetadata(rogue: IRogue, list: string, metadata: List['metadata']) {
    const listObj: List = getShortCut(list)?.value || lists.value[list]
    const _id = listObj?._id
    await updateList(rogue, { list, metadata })

    if (_id) {
      await rogue.fetch(`/svc_list/lists/${_id}/metadata`, 'PUT', metadata)
    }
  }

  async function emptyListItems(rogue: IRogue, list: ShortcutListType) {
    const listObj: List = getShortCut(list)?.value || lists.value[list]

    await updateList(rogue, { list, items: [] })

    if (listObj === cart.value) {
      rogue.track.track({ type: 'removeFromCart', items: listObj.items })
      sendCartItems(rogue)
    }

    if (listObj._id) {
      rogue.fetch(`/svc_list/lists/${listObj._id}/items`, 'DELETE')
    }
  }

  function handleDeactivatedPromo(product: IProduct, rogue: IRogue) {
    // Promos have gone inactive - check if any were in the cart :(
    const missedPromos = cart.value.items.filter(item => getSimpleForBuyRequest(item, rogue)?.productId === product.productId)
    missedDeals.value.push(...missedPromos)
  }

  function clearMissedDeals() {
    missedDeals.value = []
  }

  async function refreshCart(rogue: IRogue): Promise<void> {
    if (cart.value._id) {
      const list = await rogue.fetch<List>(`/svc_list/lists/${cart.value._id}`, 'GET')
      await updateList(rogue, { list: cart.value._id, items: list.items.reverse() })
      await sendCartItems(rogue)
    }
  }

  return {
    lists,
    allLists,
    cart,
    quote,
    sfl,
    buyNow,
    wishlist,
    cartQty,
    registries,
    registryPurchases,
    missedDeals,
    clear,
    addList,
    switchActiveCart,
    updateList,
    handleCustomerChange,
    handleUpdatedPromos,
    init,
    sendCartItems,
    addListItems,
    deleteList,
    addMessage,
    removeFromListItems,
    moveListItems,
    updateListItem,
    emptyListItems,
    updateMetadata,
    createList,
    handleDeactivatedPromo,
    clearMissedDeals,
    refreshCart,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useListStore, import.meta.hot))
}
